import AbstractCustomerValueModel from '../abstract_customer_value_model';
import BenefitsChartValue from '../../charts/benefits_chart_value';
import BenefitsWaterfallChart from '../../charts/fastpath/benefits_waterfall_chart';

class FieldNationRoi extends AbstractCustomerValueModel {
  custom_chart_classes() {
    return { BenefitsChartValue, BenefitsWaterfallChart };
  }

  percent_growth_in_business() {
    return this.input.percent_growth_in_business / 100;
  }

  percent_impvt_in_customer_retention() {
    return this.input.percent_impvt_in_customer_retention / 100;
  }

  percent_rdxn_labor_costs() {
    return this.input.percent_rdxn_labor_costs / 100;
  }

  percent_rdxn_revisits() {
    return this.input.percent_rdxn_revisits / 100;
  }

  percent_rdxn_admin_time() {
    return this.input.percent_rdxn_admin_time / 100;
  }

  percent_rdxn_tracking_time() {
    return this.input.percent_rdxn_tracking_time / 100;
  }

  adoption_rate(year) {
    return 1;
  }

  annual_admin_salary() {
    return this.input.annual_admin_salary;
  }

  annual_field_service_revenue() {
    return this.annual_field_service_spend() / (1 - this.profit_margin());
  }

  annual_field_service_spend() {
    return this.input.annual_field_service_spend;
  }

  annual_subscription_rate() {
    return this.input.annual_subscription_rate / 100;
  }

  annual_tracker_salary() {
    return this.input.annual_tracker_salary;
  }

  avg_cost_per_revisit() {
    return Math.round(this.annual_field_service_spend() / this.work_orders_per_year());
  }

  burden_rate() {
    return this.input.burden_rate;
  }

  customer_retention_rate() {
    return this.input.customer_retention_rate / 100;
  }

  days_per_year() {
    return 260;
  }

  fte_manage_admin() {
    return this.input.fte_manage_admin;
  }

  fte_track_jobs() {
    return this.input.fte_track_jobs;
  }

  fully_burdened_salary(salary) {
    return salary * this.burden_rate();
  }

  implementation_fee() {
    return this.input.implementation_fee;
  }

  incremental_opex_rate_year1() {
    return this.input.incremental_opex_rate_year1 / 100;
  }

  incremental_opex_rate_year2() {
    return this.input.incremental_opex_rate_year2 / 100;
  }

  incremental_opex_rate_year3() {
    return this.input.incremental_opex_rate_year3 / 100;
  }

  monthly_admin_hours_per_fte() {
    return this.input.monthly_admin_hours_per_fte;
  }

  pct_field_work_outsourced() {
    return this.input.pct_field_work_outsourced / 100;
  }

  pct_orders_requiring_revisit() {
    return this.input.pct_orders_requiring_revisit / 100;
  }

  pct_service_spend_on_field_nation() {
    return this.input.pct_service_spend_on_field_nation / 100;
  }

  profit_margin() {
    return this.input.profit_margin / 100;
  }

  targeted_spend_with_fn() {
    return this.input.targeted_spend_with_fn;
  }

  wkly_job_tracking_hrs_per_fte() {
    return this.input.wkly_job_tracking_hrs_per_fte;
  }

  work_orders_per_year() {
    return this.input.work_orders_per_year;
  }

  year2_increase_multiplier() {
    return this.input.year2_increase_multiplier;
  }

  year3_increase_multiplier() {
    return this.input.year3_increase_multiplier / 100;
  }

  // Benefit 1
  grow_business() {
    return this.annual_field_service_revenue() * this.profit_margin()
      * this.percent_growth_in_business();
  }

  // Benefit 2
  improve_customer_retention() {
    const before = this.annual_field_service_revenue()
      * this.pct_field_work_outsourced()
      * this.pct_service_spend_on_field_nation()
      * this.customer_retention_rate()
      * this.profit_margin();

    const after = this.annual_field_service_revenue()
      * this.pct_field_work_outsourced()
      * this.pct_service_spend_on_field_nation()
      * this.customer_retention_rate() * (1 + this.percent_impvt_in_customer_retention())
      * this.profit_margin();

    return after - before;
  }

  // Benefit 3
  reduce_labor_costs(year = 1) {
    const before = this.annual_field_service_revenue()
      * this.pct_field_work_outsourced()
      * this.pct_service_spend_on_field_nation()
      * (1 - this.profit_margin());

    if (year === 1) {
      return before * this.percent_rdxn_labor_costs();
    }

    if (year === 2) {
      const benefit = (before * this.year2_increase_multiplier()) - ((1 - this.percent_rdxn_labor_costs()) * before * this.year2_increase_multiplier());
      return benefit
    }

    const year3_modifier = (1 + this.year3_increase_multiplier());
    return (before * this.year2_increase_multiplier() * year3_modifier) - ((1 - this.percent_rdxn_labor_costs()) * (before * this.year2_increase_multiplier() * year3_modifier))
  }

  // Benefit 4
  reduce_site_revisits() {
    return this.work_orders_per_year()
      * this.pct_field_work_outsourced()
      * this.pct_service_spend_on_field_nation()
      * this.pct_orders_requiring_revisit()
      * this.avg_cost_per_revisit()
      * this.percent_rdxn_revisits();
  }

  // Benefit 5
  reduce_time_on_admin() {
    return this.fte_manage_admin()
      * this.monthly_admin_hours_per_fte()
      * this.fully_burdened_salary(this.annual_admin_salary()) / 8 / this.days_per_year()
      * 12
      * this.percent_rdxn_admin_time();
  }

  // Benefit 6
  reduce_time_reviewing_jobs() {
    return this.fte_track_jobs()
      * this.wkly_job_tracking_hrs_per_fte()
      * this.fully_burdened_salary(this.annual_tracker_salary()) / 8 / this.days_per_year()
      * 52
      * this.percent_rdxn_tracking_time();
  }

  all_benefits() {
    return [
      'grow_business',
      'improve_customer_retention',
      'reduce_labor_costs',
      'reduce_site_revisits',
      'reduce_time_on_admin',
      'reduce_time_reviewing_jobs'
    ];
  }

  value_drivers() {
    return [
      'grow_revenue',
      'reduce_cogs',
      'streamline_operations'
    ]
  }

  grow_revenue() {
    return this.grow_business()
      + this.improve_customer_retention();
  }

  reduce_cogs(year = 1) {
    return this.reduce_labor_costs(year)
      + this.reduce_site_revisits(year);
  }

  streamline_operations() {
    return this.reduce_time_on_admin()
      + this.reduce_time_reviewing_jobs();
  }

  // override parent
  cumulative_growth(benefit, year) {
    const benefits_with_growth = [
      'reduce_time_on_admin',
      'reduce_time_reviewing_jobs',
      'streamline_operations'
    ];

    if (benefits_with_growth.includes(benefit)) {
      return this.salary_growth_factor(year);
    }

    return super.cumulative_growth(benefit, year);
  }

  salary_growth_factor(year) {
    return 1.03 ** (year - 1);
  }

  annual_subscription_fee(year = 1) {
    const base = this.targeted_spend_with_fn() * this.annual_subscription_rate();

    if (year === 1) { return base; }
    if (year === 2) { return base * this.year2_increase_multiplier(); }

    return base * this.year2_increase_multiplier() * (1 + this.year3_increase_multiplier());
  }

  incremental_opex_year1() {
    return this.incremental_opex_rate(1) * this.targeted_spend_with_fn();
  }

  incremental_opex_year2() {
    return this.incremental_opex_rate(2) * this.targeted_spend_with_fn();
  }

  incremental_opex_year3() {
    return this.incremental_opex_rate(3) * this.targeted_spend_with_fn();
  }


  incremental_opex_rate(year = 1) {
    switch (year) {
      case 1:
        return this.incremental_opex_rate_year1();
      case 2:
        return this.incremental_opex_rate_year2();
      default:
        return this.incremental_opex_rate_year3();
    }
  }

  total_annual_investment_for_fn() {
    return this.total_annual_investment(1);
  }

  total_annual_investment(year = 1) {
    const base = this.annual_subscription_fee(year)
      + (this.incremental_opex_rate(year) * this.targeted_spend_with_fn());

    if (year === 1) { return base + this.implementation_fee(); }

    return base;
  }

  grow_business_label() {
    return this.input.grow_business_label;
  }

  improve_customer_retention_label() {
    return this.input.improve_customer_retention_label;
  }

  reduce_labor_costs_label() {
    return this.input.reduce_labor_costs_label;
  }

  reduce_site_revisits_label() {
    return this.input.reduce_site_revisits_label;
  }

  reduce_time_on_admin_label() {
    return this.input.reduce_time_on_admin_label;
  }

  reduce_time_reviewing_jobs_label() {
    return this.input.reduce_time_on_admin_label;
  }

  grow_revenue_label() {
    return this.input.grow_revenue_label;
  }

  reduce_cogs_label() {
    return this.input.reduce_cogs_label;
  }

  streamline_operations_label() {
    return this.input.streamline_operations_label;
  }

  // The next methods are for the custom roi_summary partial for FieldNation
  annual_subscription_fee_for_summary() {
    const term = parseInt(this.input.financing_term);

    let total = 0;
    for (let i=0; i<term; i++) {
      total += this.annual_subscription_fee(i+1);
    }

    return total;
  }

  incremental_opex_for_summary() {
    const term = parseInt(this.input.financing_term);

    let total = 0;
    for (let i=0; i<term; i++) {
      total += this.incremental_opex_rate(i+1) * this.targeted_spend_with_fn();
    }

    return total;
  }

  total_annual_investment_for_summary() {
    const term = parseInt(this.input.financing_term);

    let total = 0;
    for (let i=0; i<term; i++) {
      total += this.total_annual_investment(i+1);
    }

    return total;
  }
}

export default FieldNationRoi;
